<template>
  <div class="auth-layout">
    <main
      class="auth-layout__container grid-container grid-container--responsive"
    >
      <div class="auth-layout__left-side">
        <img src="/auth/phone.png" alt="">
      </div>
      <div class="auth-layout__right-side">
        <div
          class="auth-layout__card"
          :class="{ 'auth-layout__card--centered': isPinRoute }"
        >
          <slot />
        </div>
      </div>
    </main>
    <img src="/auth/text-logo-light.svg" alt="" class="auth-layout__logo">
  </div>

  <div class="backdrop" />
</template>

<script setup lang="ts">
import {
  getPinUnlockLocation,
  isAuthFlowRoute,
  isAuthSectionRoute,
  isPinUiRoute,
  isPinUnlockRoute,
  useAuth,
} from '~/entities/auth'
import { permissions } from '~/shared/lib'

const route = useRoute()
const { isAuthenticated, shouldRequirePinUnlock } = useAuth()

const isPinRoute = computed(() => isPinUiRoute(route.path))

watch(
  () => [isAuthenticated.value, route.path, shouldRequirePinUnlock()] as const,
  async ([authenticated, routePath, requiresPinUnlock]) => {
    if (!authenticated || !routePath) {
      return
    }

    if (requiresPinUnlock && !isPinUnlockRoute(routePath)) {
      await navigateTo(getPinUnlockLocation({
        fullPath: route.fullPath,
        queryRedirect: route.query.redirect,
      }), { replace: true })
      return
    }

    if (isAuthFlowRoute(routePath)) {
      return
    }

    if (isAuthSectionRoute(routePath)) {
      await navigateTo(permissions.WORKSPACE.ROUTE)
    }
  },
  { immediate: true, flush: 'post' },
)
</script>

<style scoped lang="scss">
.auth-layout {
  background-image: url("/auth/background.webp");
  background-size: cover;
  background-position: center center;
  position: relative;

  &__container {
    min-height: 100dvh;
    padding-top: 24px;
    padding-bottom: 24px;
  }

  &__left-side,
  &__right-side {
    height: inherit;
  }

  &__left-side {
    grid-column: 1 / 7;

    @include bp-media('xs', 'sm', 'md', 'lg') {
      grid-column: 1 / 6;
    }

    @include bp-media('xs', 'sm') {
      display: none;
    }

    padding-bottom: 48px;
    display: flex;
    justify-content: center;
    align-items: flex-end;

    img {
      display: block;
      height: auto;
      object-fit: cover;
      width: calc(294px * 100% / 672px);
      min-width: 294px;
    }
  }

  &__right-side {
    grid-column: 7 / 12;

    @include bp-media('xs', 'sm', 'md', 'lg') {
      grid-column: 6 / 12;
    }

    @include bp-media('xs', 'sm') {
      grid-column: span 6;
    }

    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__card {
    border-radius: var(--radius-lg);
    background-color: var(--color-surface-bg);
    min-height: 512px;
    max-height: 658px;
    height: 100%;
    max-width: 566px;
    width: 100%;
    padding: rem(72px) rem(60px);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: rem(24px);
    scrollbar-width: none;
    z-index: 1;

    @include bp-media('xs') {
      height: auto;
      max-height: none;
    }

    &::-webkit-scrollbar {
      display: none;
    }

    @include bp-media('xs', 'sm', 'md') {
      padding: rem(56px) rem(44px);
    }

    @include bp-media('xs') {
      padding: 40px 16px;
    }

    &--centered {
      justify-content: center;
    }
  }

  &__logo {
    position: absolute;
    bottom: rem(24px);
    right: rem(24px);

    @include bp-media('xs', 'sm') {
      right: 50%;
      transform: translateX(50%);
    }
  }
}
</style>
